// MCHD Room Scheduler - User Management Page
// Main user management interface for administrators

import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Grid,
  Card,
  Group,
  Button,
  TextInput,
  Select,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  Stack,
  Pagination,
  Alert,
  Loader,
  Center,
  Table,
  ScrollArea,
  Avatar,
  Tooltip,
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconFilter,
  IconDots,
  IconEdit,
  IconUserCheck,
  IconUserX,
  IconShield,
  IconUsers,
  IconUser,
  IconSettings,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useUsers, useUserStatsFromData, useDeactivateUser, useActivateUser, useChangeUserRole } from '../../../hooks/admin/useUsers.js';
import { useUserManagement } from '../../../hooks/admin/useUserManagement';
import { useAuth } from '../../../hooks/useAuth';
import { User } from '../../../api/endpoints/users.js';
import DynamicRefreshButton from '../../../components/atoms/DynamicRefreshButton';
import { UserForm } from '../../../components/organisms/UserManagement/UserForm';

// =============================================================================
// TYPES
// =============================================================================

interface UserFilters {
  search: string;
  role: string;
  isActive: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function UserManagement(): JSX.Element {
  // State
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: '',
    isActive: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'view' | 'edit'>('view');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState<'ADMIN' | 'COORDINATOR' | 'DOCTOR'>('DOCTOR');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Check if search is in progress
  const isSearching = filters.search !== debouncedSearch;

  // Hooks
  const { data: usersData, isLoading, error, refetch } = useUsers({
    page: currentPage,
    limit: 10,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(filters.role && { role: filters.role as 'ADMIN' | 'COORDINATOR' | 'DOCTOR' }),
    ...(filters.isActive && { isActive: filters.isActive === 'true' }),
  });

  const { getStoredToken } = useAuth();
  const { createUser } = useUserManagement(getStoredToken() || '');
  
  // Calculate stats from existing users data to avoid duplicate API calls
  const stats = useUserStatsFromData(usersData?.users || []);
  const deactivateUserMutation = useDeactivateUser();
  const activateUserMutation = useActivateUser();
  const changeRoleMutation = useChangeUserRole();

  // Handlers
  const handleFilterChange = (key: keyof UserFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleUserAction = (user: User, action: string) => {
    setSelectedUser(user);
    switch (action) {
      case 'view':
        setModalMode('view');
        setShowUserModal(true);
        break;
      case 'edit':
        setModalMode('edit');
        setShowUserModal(true);
        break;
      case 'role':
        setNewRole(user.role);
        setShowRoleModal(true);
        break;
      case 'deactivate':
        deactivateUserMutation.mutate(user.id);
        break;
      case 'activate':
        activateUserMutation.mutate(user.id);
        break;
    }
  };

  const handleRoleChange = () => {
    if (selectedUser) {
      changeRoleMutation.mutate({ userId: selectedUser.id, role: newRole });
      setShowRoleModal(false);
    }
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setModalMode('view');
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowUserModal(true);
  };

  const handleUserSubmit = (userData: any) => {
    if (modalMode === 'create') {
      createUser(userData);
      handleCloseUserModal(); // Close modal immediately since createUser doesn't return a promise
    }
    // TODO: Implement edit user functionality when needed
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'red';
      case 'COORDINATOR': return 'blue';
      case 'DOCTOR': return 'green';
      default: return 'gray';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'green' : 'red';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  // Loading state
  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>Loading users...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Users"
          color="red"
          variant="light"
        >
          {error instanceof Error ? error.message : String(error)}
        </Alert>
      </Container>
    );
  }

  const users = usersData?.users || [];
  const pagination = usersData?.pagination;
  
  // Get the latest user data for the modal
  const currentUser = selectedUser ? users.find(user => user.id === selectedUser.id) || selectedUser : null;

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <div>
          <Title order={1} mb="xs">
            User Management
          </Title>
          <Text c="dimmed">
            Manage user accounts, roles, and permissions
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleCreateUser}
        >
          Add User
        </Button>
      </Group>

      {/* Statistics Cards */}
      <Grid mb="xl">
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed" fw={500}>
                  Total Users
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalUsers}
                </Text>
              </div>
              <IconUsers size={32} color="var(--mantine-color-blue-6)" />
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed" fw={500}>
                  Active Users
                </Text>
                <Text size="xl" fw={700} c="green">
                  {stats.activeUsers}
                </Text>
              </div>
              <IconUserCheck size={32} color="var(--mantine-color-green-6)" />
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed" fw={500}>
                  Administrators
                </Text>
                <Text size="xl" fw={700} c="red">
                  {stats.adminUsers}
                </Text>
              </div>
              <IconShield size={32} color="var(--mantine-color-red-6)" />
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={3}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between">
              <div>
                <Text size="sm" c="dimmed" fw={500}>
                  Coordinators
                </Text>
                <Text size="xl" fw={700} c="blue">
                  {stats.coordinatorUsers}
                </Text>
              </div>
              <IconSettings size={32} color="var(--mantine-color-blue-6)" />
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Enhanced Search and Filters */}
      <Card shadow="sm" padding="xl" radius="md" withBorder mb="xl">
        <Stack gap="md">
          {/* Large Search Input */}
          <TextInput
            placeholder="Search users by name, email, or role..."
            leftSection={<IconSearch size={20} />}
            rightSection={isSearching ? <Loader size="xs" /> : undefined}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            size="lg"
            style={{ 
              fontSize: '16px',
              '& input': {
                height: '48px',
                fontSize: '16px',
                fontWeight: 500,
              }
            }}
          />
          
          {/* Filter Row */}
          <Group gap="md" wrap="wrap">
          <Select
            placeholder="All Roles"
            data={[
              { value: '', label: 'All Roles' },
              { value: 'ADMIN', label: 'Administrator' },
              { value: 'COORDINATOR', label: 'Coordinator' },
              { value: 'DOCTOR', label: 'Doctor' },
            ]}
            value={filters.role}
            onChange={(value) => handleFilterChange('role', value || '')}
            leftSection={<IconFilter size={16} />}
          />
          <Select
            placeholder="All Status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
            value={filters.isActive}
            onChange={(value) => handleFilterChange('isActive', value || '')}
            leftSection={<IconUser size={16} />}
          />
          <DynamicRefreshButton
            variant="light"
            onRefresh={async () => { await refetch(); }}
            isLoading={isLoading}
            size="sm"
            color="blue"
          />
          </Group>
        </Stack>
      </Card>

      {/* Users Table */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text size="lg" fw={600}>
            Users ({pagination?.total || 0})
          </Text>
        </Group>

        {users.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <IconUsers size={48} color="var(--mantine-color-gray-4)" />
              <Text c="dimmed">No users found. Add the first user to begin.</Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => setModalMode('create')}
              >
                Add User
              </Button>
            </Stack>
          </Center>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="md" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => (
                  <Table.Tr key={user.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Avatar
                          size="md"
                          radius="xl"
                          color={getRoleColor(user.role)}
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text fw={600} size="sm">
                            {user.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {user.email}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getRoleColor(user.role)} variant="light" size="sm">
                        {user.role}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(user.isActive)} variant="light" size="sm">
                        {getStatusText(user.isActive)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="View Details">
                          <Button
                            variant="light"
                            size="xs"
                            onClick={() => handleUserAction(user, 'view')}
                          >
                            View
                          </Button>
                        </Tooltip>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => handleUserAction(user, 'edit')}
                            >
                              Edit User
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconShield size={14} />}
                              onClick={() => handleUserAction(user, 'role')}
                            >
                              Change Role
                            </Menu.Item>
                            <Menu.Divider />
                            {user.isActive ? (
                              <Menu.Item
                                leftSection={<IconUserX size={14} />}
                                color="red"
                                onClick={() => handleUserAction(user, 'deactivate')}
                              >
                                Deactivate
                              </Menu.Item>
                            ) : (
                              <Menu.Item
                                leftSection={<IconUserCheck size={14} />}
                                color="green"
                                onClick={() => handleUserAction(user, 'activate')}
                              >
                                Activate
                              </Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <Group justify="center" mt="xl">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={pagination.pages}
              size="sm"
            />
          </Group>
        )}
      </Card>

      {/* User Modal - Create/Edit Form or View Details */}
      {modalMode === 'create' || modalMode === 'edit' ? (
        <UserForm
          opened={showUserModal}
          onClose={handleCloseUserModal}
          onSubmit={handleUserSubmit}
          user={selectedUser}
          mode={modalMode}
        />
      ) : (
        <Modal
          opened={showUserModal}
          onClose={handleCloseUserModal}
        title={
          <Group gap="md">
            <Avatar
              size="lg"
              radius="xl"
              color={currentUser ? getRoleColor(currentUser.role) : 'gray'}
            >
              {currentUser?.name.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Text size="lg" fw={600}>
                {currentUser?.name}
              </Text>
              <Text size="sm" c="dimmed">
                User Details
              </Text>
            </div>
          </Group>
        }
        size="lg"
        centered
      >
        {currentUser && (
          <Stack gap="xl">
            {/* User Info Cards */}
            <Grid>
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Stack gap="xs">
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                      Contact Information
                    </Text>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Email Address</Text>
                      <Text size="sm" c="dimmed">{currentUser.email}</Text>
                    </div>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Stack gap="xs">
                    <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                      Account Status
                    </Text>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Role</Text>
                      <Badge color={getRoleColor(currentUser.role)} size="sm">
                        {currentUser.role}
                      </Badge>
                    </div>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Status</Text>
                      <Badge color={getStatusColor(currentUser.isActive)} size="sm">
                        {getStatusText(currentUser.isActive)}
                      </Badge>
                    </div>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Activity Information */}
            <Card padding="md" radius="md" withBorder>
              <Stack gap="md">
                <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                  Activity Information
                </Text>
                <Grid>
                  <Grid.Col span={6}>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Last Login</Text>
                      <Text size="sm" c="dimmed">
                        {currentUser.lastLogin 
                          ? new Date(currentUser.lastLogin).toLocaleString()
                          : 'Never logged in'
                        }
                      </Text>
                    </div>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <div>
                      <Text size="sm" fw={500} mb={4}>Account Created</Text>
                      <Text size="sm" c="dimmed">
                        {new Date(currentUser.createdAt).toLocaleString()}
                      </Text>
                    </div>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>

            {/* Action Buttons */}
            <Group justify="flex-end" gap="sm">
              <Button
                variant="light"
                onClick={() => handleUserAction(currentUser, 'edit')}
                leftSection={<IconEdit size={16} />}
              >
                Edit User
              </Button>
              <Button
                variant="light"
                onClick={() => handleUserAction(currentUser, 'role')}
                leftSection={<IconShield size={16} />}
              >
                Change Role
              </Button>
              {currentUser.isActive ? (
                <Button
                  variant="light"
                  color="red"
                  onClick={() => handleUserAction(currentUser, 'deactivate')}
                  leftSection={<IconUserX size={16} />}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="light"
                  color="green"
                  onClick={() => handleUserAction(currentUser, 'activate')}
                  leftSection={<IconUserCheck size={16} />}
                >
                  Activate
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
      )}

      {/* Change Role Modal */}
      <Modal
        opened={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        title="Change User Role"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Change role for <strong>{selectedUser?.name}</strong>
          </Text>
          <Select
            label="New Role"
            data={[
              { value: 'ADMIN', label: 'Administrator' },
              { value: 'COORDINATOR', label: 'Coordinator' },
              { value: 'DOCTOR', label: 'Doctor' },
            ]}
            value={newRole}
            onChange={(value) => setNewRole(value as 'ADMIN' | 'COORDINATOR' | 'DOCTOR')}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRoleChange}
              loading={changeRoleMutation.isPending}
            >
              Change Role
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}